import React, { PropsWithChildren } from "react";
import { useRouter } from "next/router";

type ContextProps = {
  refreshConfig: () => void;
  validSession: boolean;
  setValidSession: (status: boolean) => void;
  url: URL | undefined;
  setUrl: (url: URL) => void;
  shop: string;
  setShop: (shop: string) => void;
  index: string;
  setIndex: (index: string) => void;
  returnUrl: string;
  setReturnUrl: (url: string) => void;
  storeUrl: string;
  setStoreUrl: (url: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  collections: string[];
  urlHandle: string;
  setURLHandle: (handle: string) => void;
  isSearch: boolean;
  isCollection: boolean;
  viewUrl: string;
  pageTitle: string;
  showModalConfig: boolean;
  setShowModalConfig: (value: boolean) => void;
  showModalIndices: boolean;
  setShowModalIndices: (value: boolean) => void;
  market: string | undefined;
  setMarket: (value: string) => void;
};

export const AppContext = React.createContext<ContextProps | undefined>(undefined);

export function AppProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const [validSession, setValidSession] = React.useState<boolean>(false);
  const [endpoint, setEndpoint] = React.useState<string>("");
  const [url, setUrl] = React.useState<URL>();
  const [shop, setShop] = React.useState<string>("");
  const [index, setIndex] = React.useState<string>("");
  const [returnUrl, setReturnUrl] = React.useState<string>("");
  const [storeUrl, setStoreUrl] = React.useState<string>("");
  const [urlHandle, setURLHandle] = React.useState<string>("");
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [collections, setCollections] = React.useState<string[]>([]);
  const [showModalConfig, setShowModalConfig] = React.useState<boolean>(false);
  const [showModalIndices, setShowModalIndices] = React.useState<boolean>(false);
  const [market, setMarket] = React.useState<string>();

  React.useEffect(() => {
    const url = new URL(window.location.href);
    setUrl(url);
    setSearchTerm(url.searchParams.get("q") || "");
    setURLHandle(url.pathname.split("/")?.[2] || "");
  }, [router.pathname, router.query]);

  React.useEffect(() => {
    if (
      !!url?.searchParams.get("endpoint") &&
      !!url?.searchParams.get("shop") &&
      !!url?.searchParams.get("index") &&
      !!url?.searchParams.get("returnUrl")
    ) {
      sessionStorage.setItem("endpoint", `${url?.searchParams.get("endpoint")}`);
      sessionStorage.setItem("shop", `${url?.searchParams.get("shop")}`);
      sessionStorage.setItem("index", `${url?.searchParams.get("index")}`);
      sessionStorage.setItem("returnUrl", `${url?.searchParams.get("returnUrl")}`);
      setValidSession(true);

      if (typeof window !== "undefined") {
        router.replace(`${url?.pathname}${url?.searchParams.get("q") ? `?q=${url?.searchParams.get("q")}` : ""}`);
      }
    }

    if (
      !!sessionStorage.getItem("endpoint") &&
      !!sessionStorage.getItem("shop") &&
      !!sessionStorage.getItem("index") &&
      !!sessionStorage.getItem("returnUrl")
    ) {
      setValidSession(true);
    }
  }, [url, router]);

  React.useEffect(() => {
    setEndpoint(sessionStorage.getItem("endpoint") || "");
    setShop(sessionStorage.getItem("shop") || "");
    setIndex(sessionStorage.getItem("index") || "");
    setReturnUrl(sessionStorage.getItem("returnUrl") || "");
    setStoreUrl(`https://${sessionStorage.getItem("shop") || ""}`);
  }, [validSession]);

  React.useEffect(() => {
    const getCollections = async () => {
      const response = await fetch(`${endpoint}/${index}/_search`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          size: 0,
          aggs: { collections: { terms: { field: "collection_handles.keyword", size: 10000 } } },
        }),
      }).then((response) => response.json());

      const collectionHandles = response.aggregations.collections.buckets
        .map(({ key }: { key: string }) => key)
        .sort((a: any, b: any) => a.localeCompare(b));

      setCollections(collectionHandles);
    };
    if (index && index !== "") getCollections();
  }, [index, endpoint]);

  const isSearch = React.useMemo(() => url?.pathname.includes("/search") || false, [url]);
  const isCollection = React.useMemo(() => url?.pathname.includes("/collections") || false, [url]);
  const isProduct = React.useMemo(() => url?.pathname.includes("/product") || false, [url]);

  const viewUrl = React.useMemo(
    () =>
      isCollection && urlHandle
        ? `${storeUrl}/collections/${urlHandle}`
        : isSearch
          ? `${storeUrl}/search?q=${searchTerm}`
          : isProduct
            ? `${storeUrl}/products/${urlHandle}`
            : "",
    [storeUrl, isCollection, urlHandle, isSearch, searchTerm, isProduct]
  );

  const pageTitle = React.useMemo(
    () =>
      `${
        isCollection && urlHandle
          ? `: ${urlHandle}`
          : isSearch && searchTerm
            ? `: ${searchTerm}`
            : isProduct && urlHandle
              ? `: ${urlHandle}`
              : ""
      }`,
    [storeUrl, isCollection, urlHandle, isSearch, searchTerm, isProduct]
  );

  const refreshConfig = () => {
    sessionStorage.removeItem("reactify-search:config");
    window.location.href = `${window.location.origin}${window.location.pathname}?shop=${shop}&index=${index}&returnUrl=${returnUrl}&nocache=true`;
  };

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      refreshConfig,
      validSession,
      setValidSession,
      url,
      setUrl,
      endpoint,
      setEndpoint,
      shop,
      setShop,
      index,
      setIndex,
      returnUrl,
      setReturnUrl,
      storeUrl,
      setStoreUrl,
      searchTerm,
      setSearchTerm,
      collections,
      urlHandle,
      setURLHandle,
      isSearch,
      isCollection,
      viewUrl,
      pageTitle,
      showModalConfig,
      setShowModalConfig,
      showModalIndices,
      setShowModalIndices,
      market,
      setMarket,
    }),
    [
      refreshConfig,
      validSession,
      setValidSession,
      url,
      setUrl,
      endpoint,
      setEndpoint,
      shop,
      setShop,
      index,
      setIndex,
      returnUrl,
      setReturnUrl,
      storeUrl,
      setStoreUrl,
      searchTerm,
      setSearchTerm,
      collections,
      urlHandle,
      setURLHandle,
      isSearch,
      isCollection,
      viewUrl,
      pageTitle,
      showModalConfig,
      setShowModalConfig,
      showModalIndices,
      setShowModalIndices,
      market,
      setMarket,
    ]
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
}

export const useAppContext = (): ContextProps => ({ ...React.useContext(AppContext) }) as ContextProps;
